import { useCallback, useEffect, useState } from "react";
import { Mail, Pencil, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", email: "", label: "Investor", outlets: [], active: true };
const LABELS = ["Owner", "Investor", "Owner Franchise", "Kepala Cabang"];
const input = "px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm w-full";

const RecipientForm = ({ outlets, initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (code) => set("outlets", f.outlets.includes(code) ? f.outlets.filter((c) => c !== code) : [...f.outlets, code]);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: f.name, email: f.email, label: f.label, outlets: f.outlets, active: f.active };
      if (f.id) await api.put(`/admin/report-recipients/${f.id}`, body); else await api.post("/admin/report-recipients", body);
      toast.success("Penerima disimpan");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="card-paper p-5 grid sm:grid-cols-2 gap-3 text-sm" data-testid="recipient-form">
      <input required placeholder="Nama" value={f.name} onChange={(e) => set("name", e.target.value)} className={input} data-testid="recipient-name-input" />
      <input required type="email" placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} className={input} data-testid="recipient-email-input" />
      <select value={f.label} onChange={(e) => set("label", e.target.value)} className={input} data-testid="recipient-label-select">
        {LABELS.map((l) => <option key={l}>{l}</option>)}
      </select>
      <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="recipient-active-checkbox" /> Aktif menerima email</label>
      <div className="sm:col-span-2">
        <div className="text-xs text-muted2 mb-1.5">Gerai yang dilaporkan (kosongkan = semua gerai)</div>
        <div className="flex flex-wrap gap-2">
          {outlets.map((o) => (
            <button type="button" key={o.code} onClick={() => toggle(o.code)} data-testid={`recipient-outlet-${o.code}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${f.outlets.includes(o.code) ? "bg-forest text-white" : "bg-sage-100 text-ink"}`}>{o.code} · {o.name}</button>
          ))}
        </div>
      </div>
      <div className="sm:col-span-2 flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-full bg-sage-100 font-semibold" data-testid="recipient-cancel-button">Batal</button>
        <button disabled={saving} className="btn-forest px-4 py-2" data-testid="recipient-save-button">{saving ? "Menyimpan..." : "Simpan"}</button>
      </div>
    </form>
  );
};

const StatusLine = ({ r }) => {
  if (!r.last_status) return <span className="text-muted2">Belum pernah dikirim</span>;
  const ok = r.last_status === "terkirim";
  return <span className={ok ? "text-emerald-700" : "text-red-700"} data-testid={`recipient-status-${r.id}`}>{ok ? "Terkirim" : `Gagal: ${r.last_error}`} · laporan {r.last_sent_month}</span>;
};

const RecipientRow = ({ r, onEdit, onReload }) => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm(`Kirim laporan bulan lalu ke ${r.email} sekarang?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/report-recipients/${r.id}/send`); toast.success(`Laporan ${data.month} terkirim ke ${r.email}`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); onReload(); }
  };
  const remove = async () => {
    if (!window.confirm(`Hapus ${r.email} dari penerima laporan?`)) return;
    await api.delete(`/admin/report-recipients/${r.id}`).catch((e) => toast.error(errMsg(e)));
    onReload();
  };
  return (
    <div className="card-paper p-4 flex flex-wrap items-center gap-3 text-sm" data-testid={`recipient-row-${r.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold">{r.name} <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-sage-100 ml-1">{r.label}</span>{!r.active && <span className="text-xs text-red-700 ml-2">nonaktif</span>}</div>
        <div className="text-muted2 break-all">{r.email}</div>
        <div className="text-xs mt-1">Gerai: <b>{r.outlets.length ? r.outlets.join(", ") : "Semua gerai"}</b> · <StatusLine r={r} /></div>
      </div>
      <div className="flex gap-2">
        <button onClick={send} disabled={busy} className="px-3 py-2 rounded-full bg-sage-100 font-semibold text-xs flex items-center gap-1.5" data-testid={`recipient-send-${r.id}`}><Send className="w-3.5 h-3.5" />{busy ? "Mengirim..." : "Kirim sekarang"}</button>
        <button onClick={() => onEdit(r)} className="p-2 rounded-full bg-sage-100" aria-label="Ubah" data-testid={`recipient-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
        <button onClick={remove} className="p-2 rounded-full bg-red-50 text-red-700" aria-label="Hapus" data-testid={`recipient-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const ReportRecipients = ({ outlets }) => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get("/admin/report-recipients").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="report-recipients">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif font-semibold text-2xl flex items-center gap-2"><Mail className="w-6 h-6" /> Email laporan bulanan</h2>
          <p className="text-sm text-muted2">Dikirim otomatis setiap tanggal 1 pukul 08.00 WIB berisi laporan bulan sebelumnya: penjualan, PB1, perbandingan bulan lalu, menu terlaris & jarang laku.</p>
        </div>
        {!editing && <button onClick={() => setEditing(EMPTY)} className="btn-forest px-4 py-2 text-sm" data-testid="recipient-add-button">+ Tambah penerima</button>}
      </div>
      {editing && <div className="mb-4"><RecipientForm outlets={outlets} initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} /></div>}
      <div className="space-y-3">
        {list.length === 0 && <p className="text-center text-muted2 py-10" data-testid="recipients-empty">Belum ada penerima laporan.</p>}
        {list.map((r) => <RecipientRow key={r.id} r={r} onEdit={setEditing} onReload={load} />)}
      </div>
    </div>
  );
};
